import { Fragment, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import API_CONSTRUCTOR from "../../models/api-model";
import PropertyAPIs from "../../models/property";
import { get } from "lodash";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  CardMedia,
  Skeleton,
  Typography,
} from "@mui/material";
import HouseFlippingCalculator from "../../components/calculator/index";
import HorizontalScrollGallery from "../../components/Images";
import { NearbyInsights } from "./nearby-insights";
import { COLORS } from "../../utils/colors";
import { CompareProperty } from "./compare-property";
import MapView from "../../components/MapView";

const apiModel = API_CONSTRUCTOR("https://api.propertyreach.com");
const propertyApi = new PropertyAPIs(apiModel);

const Property = () => {
  const [property, setProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { propertyId } = useParams();
  const [marketValuePerSqft, setMarketValuePerSqft] = useState(0);

  const {
    address = "",
    assessedValue = 0,
    bedrooms = "N/A",
    city = "",
    county = "",
    latitude,
    listing = {},
    longitude,
    loanAmount = 0,
    loanBalance = 0,
    lotSquareFeet = 0,
    photoUrls = [],
    photosAvailable = false,
    pool = false,
    state = "",
    preForeclosure = false,
    bankruptcy = false,
    vacant = false,
    onMarket = false,
    ownerNames = "",
  } = { ...property };
  const { amount, squareFeet = 1 } = listing || {};
  let { estimatedValue = 1, estimatedRentAmount = 0 } = { ...property };
  estimatedValue =
    estimatedValue < amount
      ? estimatedValue
      : amount * 1.2 > estimatedValue
      ? amount
      : estimatedValue;
  estimatedRentAmount =
    (estimatedValue * 0.045) / 12 > estimatedRentAmount
      ? Math.ceil((estimatedValue * 0.045) / 12)
      : estimatedRentAmount;

  const propertyDetails = [
    {
      details: [
        {
          label: "City",
          value: city,
        },
        {
          label: "County",
          value: county,
        },
        {
          label: "State",
          value: state,
        },
        {
          label: "Owner",
          value: ownerNames,
        },
      ],
    },
    {
      details: [
        {
          label: "Open loan balance",
          value: `$${loanBalance}`,
        },
        {
          label: "Estimated value",
          value: `$${estimatedValue}`,
        },
        {
          label: "Estimated rent",
          value: `$${estimatedRentAmount} (per month)`,
        },
      ],
    },
    {
      details: [
        {
          label: "Area",
          value: squareFeet === 0 ? "N/A" : `${squareFeet}sqft`,
        },
        {
          label: "Bedrooms",
          value: bedrooms,
        },
        {
          label: "Pool",
          value: pool ? "Yes" : "No",
        },
      ],
    },
  ];

  const StatusDetails = [
    {
      title: "Property status",
      details: [
        {
          label: "For sale",
          value: onMarket ? "Yes" : "No",
        },
        {
          label: "Vacant",
          value: vacant ? "Yes" : "No",
        },
      ],
    },
    {
      details: [
        {
          label: "Bankruptcy",
          value: bankruptcy ? "Yes" : "No",
        },
        {
          label: "Pre foreclosure",
          value: preForeclosure ? "Yes" : "No",
        },
      ],
    },
  ];

  useEffect(() => {
    setMarketValuePerSqft(Math.ceil((1.3 * estimatedValue) / squareFeet));
  }, [property]);

  useEffect(() => {
    // Fetch property details using propertyId
    // Example: fetchPropertyDetails(propertyId);
    setIsLoading(true);
    setProperty(null);
    setError(null);

    propertyApi
      .getPropertyDetails(propertyId)
      .then((result) => {
        const property = get(result, "property", null);
        const meta = get(result, "meta", {});
        const { status } = meta;
        if (property) {
          setProperty(property);
        }

        if (status !== 200) {
          const { message } = meta;
          setError(message);
        }
      })
      .catch((error) => {
        console.error("Error fetching property details:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [propertyId]);

  if (isLoading) {
    return (
      <Box padding={2}>
        <Box>
          <Skeleton width={700} height={50} />
        </Box>
        <Box sx={{ display: "flex", gap: 2, marginTop: -5 }}>
          <Skeleton width={300} height={300} />
          <Skeleton width={300} height={300} />
          <Skeleton width={300} height={300} />
        </Box>
      </Box>
    );
  }

  return (
    <Box padding={2}>
      {error ? (
        <Box>
          <Typography>{error}</Typography>
          <Link color="neutral" to={"/"} href="/">
            <ArrowBackIcon /> Go back home
          </Link>
        </Box>
      ) : null}
      {property && (
        <Box>
          <Breadcrumbs aria-label="breadcrumbs" style={{ marginBottom: 20 }}>
            <Link
              color={COLORS.SURFACE_1}
              style={{ color: COLORS.SURFACE_1 }}
              to={"/"}
              href="/"
            >
              Home
            </Link>
            <Typography variant="div" gutterBottom>
              {address}
            </Typography>
          </Breadcrumbs>
          <Box>
            {photosAvailable ? (
              <Box sx={{ display: "flex", overflow: "auto", mb: 4 }}>
                <HorizontalScrollGallery images={photoUrls} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 2,
                  borderRadius: 2,
                  mb: 4,
                }}
              >
                <MapView
                  latitude={latitude}
                  longitude={longitude}
                  width={400}
                  height={400}
                />
              </Box>
            )}
            <Box sx={{ flexGrow: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 4,
                  flexWrap: "wrap",
                }}
              >
                <Card>
                  <CardContent>
                    {propertyDetails.map(({ details = [] }, index) => {
                      return (
                        <Box
                          sx={{
                            ...style.detailsCardRow,
                            ...(index === propertyDetails.length - 1
                              ? { mb: 0, border: "none" }
                              : {}),
                          }}
                        >
                          {details.map(({ label, value }) => {
                            return (
                              <Box>
                                <Typography
                                  variant="body1"
                                  style={{ fontWeight: 800 }}
                                >
                                  {label}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ color: COLORS.SURFACE_1 }}
                                >
                                  {value}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
                <Card style={{ marginBottom: 8 }}>
                  <CardContent>
                    {StatusDetails.map(({ title = "", details = [] }) => {
                      return (
                        <Fragment>
                          {title ? (
                            <Typography variant="h5" mb={3}>
                              Property status
                            </Typography>
                          ) : null}
                          <Box
                            sx={{
                              ...style.detailsCardRow,
                              border: "none",
                            }}
                          >
                            {details.map(({ label, value }) => {
                              return (
                                <Box>
                                  <Typography
                                    variant="body1"
                                    style={{ fontWeight: 800 }}
                                  >
                                    {label}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    style={{ color: COLORS.SURFACE_1 }}
                                  >
                                    {value}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Fragment>
                      );
                    })}
                  </CardContent>
                </Card>
              </Box>
              <HouseFlippingCalculator
                assessedValue={assessedValue}
                buildingSquareFeet={squareFeet}
                estimatedRentAmount={estimatedRentAmount}
                estimatedValue={estimatedValue}
                loanBalance={loanBalance}
                lotSquareFeet={lotSquareFeet}
                marketValuePerSqft={marketValuePerSqft}
                owner={ownerNames}
                pool={pool}
                setMarketValuePerSqft={setMarketValuePerSqft}
                property={property}
              ></HouseFlippingCalculator>
              <NearbyInsights
                property={property}
                propertyId={propertyId}
                setMarketValuePerSqft={setMarketValuePerSqft}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const style = {
  detailsCardRow: {
    borderBottom: "1px solid #dcdfe4",
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    mb: 2,
    width: "100%",
    paddingBottom: 2,
  },
};

export default Property;
